import React, { type FunctionComponent } from 'react';

import { SearchIcon } from '../icons/search-icon';
import { useTheme } from '../theme';
import { SSRdesktopSearchIconWrapper } from './ss-rdesktop-search-icon-wrapper';
import { SSRformContainer } from './ss-rform-container';
import { SSRmobileSearchIconWrapper } from './ss-rmobile-search-icon-wrapper';
import { SSRsearchInputContainer } from './ss-rsearch-input-container';
import { SSRsearchInputField } from './ss-rsearch-input-field';
import { SSRsearchInputWrapper } from './ss-rsearch-input-wrapper';
import { script } from './ssr-scripts/min-script-interactive-search-input';

export interface SearchInputSkeletonInteractiveSSRProps {
	advancedSearchUrl?: string;
	placeholder?: string;
	customScriptDataAttributes?: {
		[key: string]: string;
	};
	shouldFillContainer?: boolean;
}

/**
 * An interactive search input skeleton that can be used as a placeholder. The
 * state of this input is stored in the browser's window object. This
 * component creates a script tag which attaches event handlers to the
 * rendered input element as it is intended to be used for SSR.
 */
export const SearchInputSkeletonInteractiveSSR: FunctionComponent<
	SearchInputSkeletonInteractiveSSRProps
> = ({ advancedSearchUrl, placeholder, customScriptDataAttributes, shouldFillContainer }) => {
	const theme = useTheme();

	return (
		<SSRsearchInputWrapper shouldFillContainer={shouldFillContainer}>
			<div id="ssr-search-input-popout-container" data-ssr-placeholder="search-input">
				<SSRformContainer id="ssr-form-container">
					<form method="get" action={advancedSearchUrl}>
						<SSRsearchInputContainer
							shouldFillContainer={shouldFillContainer}
							id="ssr-input-container"
							role="search"
						>
							<SSRmobileSearchIconWrapper>
								<SearchIcon theme={theme} isExpanded={false} />
							</SSRmobileSearchIconWrapper>
							<SSRdesktopSearchIconWrapper id="ssr-search-icon-wrapper">
								<SearchIcon theme={theme} isExpanded={false} />
							</SSRdesktopSearchIconWrapper>
							<SSRsearchInputField
								id="ssr-search-input"
								name="text"
								placeholder={placeholder}
								autoComplete="off"
								aria-label={placeholder}
							/>
						</SSRsearchInputContainer>
						<script
							data-test-id="ssr-scripts-init"
							dangerouslySetInnerHTML={{ __html: script }}
							{...customScriptDataAttributes}
						/>
					</form>
				</SSRformContainer>
			</div>
		</SSRsearchInputWrapper>
	);
};
